import _ from 'lodash';
import type { ItemSelectValue } from '@/formbuilder/ItemSelectFormComponent.atom';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import type { BaseToolPersistedState } from '@/toolSelection/baseTool.store';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';

type ConditionItemSelectValue = ItemSelectValue | { id: string; name?: string };

export type ConditionProperty = {
  stringSignal: ConditionItemSelectValue;
  propertyName: string;
};

interface ConditionPropertyUpdatePayload {
  value: ConditionProperty;
  index: number;
}

const DEFAULT_PROPERTY_CONFIG: ConditionProperty = {
  stringSignal: '',
  propertyName: '',
};

export const INITIAL_PROPERTIES: ConditionProperty[] = [
  _.cloneDeep(DEFAULT_PROPERTY_CONFIG),
  _.cloneDeep(DEFAULT_PROPERTY_CONFIG),
];

interface ConditionWithPropertiesPersistedState extends BaseToolPersistedState {
  properties: ConditionProperty[];
}

export class ConditionWithPropertiesStore extends BaseToolStore {
  type = TREND_TOOLS.CONDITION_WITH_PROPERTIES;
  static readonly storeName = 'sqConditionWithPropertiesStore';

  initialize() {
    this.state = this.immutable(
      _.assign({}, BASE_TOOL_COMMON_PROPS, {
        properties: INITIAL_PROPERTIES,
      }),
    );
  }

  get properties(): ConditionProperty[] {
    return this.state.get('properties');
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @return {Object} The dehydrated state
   */
  dehydrate(): ConditionWithPropertiesPersistedState {
    return {
      ...this.getBasePersistedState(),
      properties: this.state.get('properties') as ConditionProperty[],
    };
  }

  /**
   * Rehydrates the store.
   *
   * @param {Object} dehydratedState - Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<ConditionWithPropertiesPersistedState>) {
    this.applyBasePersistedState(dehydratedState);

    if ('properties' in dehydratedState && dehydratedState.properties !== undefined) {
      this.state.set('properties', dehydratedState.properties);
    }
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    CONDITION_WITH_PROPERTIES_ADD_PROPERTY: () => {
      this.state.push('properties', { ...DEFAULT_PROPERTY_CONFIG });
    },
    CONDITION_WITH_PROPERTIES_REMOVE_PROPERTY: (index: number) => {
      if (index > -1) {
        this.state.splice('properties', [index, 1]);
      }
    },
    CONDITION_WITH_PROPERTIES_UPDATE_PROPERTY: ({ value, index }: ConditionPropertyUpdatePayload) => {
      if (index > -1) {
        // Set propertyName to string-signal
        const stringSignal = value.stringSignal;
        if (stringSignal && typeof stringSignal === 'object' && 'name' in stringSignal && stringSignal.name) {
          value.propertyName = stringSignal.name;
        }
        this.state.splice('properties', [index, 1, { ...this.state.get('properties', index), ...value }]);
      }
    },
  };
}
